/**
 * Ported from Entities/Config/ProctoringRule.cs. Adding a signal still means five edits: the
 * constant here, membership in Configurable, membership in ClientRaised if the browser raises it,
 * copy in plainLanguage.ts, and a row in the seed runner's DEFAULT_RULES.
 */
export const ProctorEventTypes = {
    TabSwitch: 'TabSwitch',
    WindowBlur: 'WindowBlur',
    FullscreenExit: 'FullscreenExit',
    Paste: 'Paste',
    ContextMenu: 'ContextMenu',
    UnloadAttempt: 'UnloadAttempt',

    /**
     * The candidate left the page or fullscreen and did not come back before the profile's limit.
     * Raised immediately before the browser submits what it has, so a report says why the interview
     * ended early rather than leaving a reviewer to infer it from a missing answer.
     */
    AwayTooLong: 'AwayTooLong',

    NoFace: 'NoFace',
    MultipleFaces: 'MultipleFaces',
    LookingAway: 'LookingAway',
    CameraLost: 'CameraLost',
    MultipleScreens: 'MultipleScreens',
    ScreenshotAttempt: 'ScreenshotAttempt',
    BlockedKeyInput: 'BlockedKeyInput',

    /**
     * Raised by the server after the interview, from gaps in the event stream, and never accepted
     * from a client: a browser that could raise this could also stay silent and then claim it had not.
     */
    ClientSilence: 'ClientSilence',

    /** Raised by the server when a snapshot contradicts what the client reported. */
    ClientTamperSuspected: 'ClientTamperSuspected',

    /** Non-violation sample. Severity 0, never deducts; carries the emotion reading. */
    PeriodicSample: 'PeriodicSample',

    /** Identity reference frame captured at session start. */
    Baseline: 'Baseline'
} as const;

export type ProctorEventType = (typeof ProctorEventTypes)[keyof typeof ProctorEventTypes];

/** Signals an admin can enable, disable and weight. Excludes the internal ones. */
export const CONFIGURABLE_EVENT_TYPES: readonly string[] = [
    ProctorEventTypes.TabSwitch,
    ProctorEventTypes.WindowBlur,
    ProctorEventTypes.FullscreenExit,
    ProctorEventTypes.Paste,
    ProctorEventTypes.ContextMenu,
    ProctorEventTypes.UnloadAttempt,
    ProctorEventTypes.AwayTooLong,
    ProctorEventTypes.NoFace,
    ProctorEventTypes.MultipleFaces,
    ProctorEventTypes.LookingAway,
    ProctorEventTypes.CameraLost,
    ProctorEventTypes.MultipleScreens,
    ProctorEventTypes.ScreenshotAttempt,
    ProctorEventTypes.BlockedKeyInput,
    ProctorEventTypes.ClientSilence,
    ProctorEventTypes.ClientTamperSuspected
];

/** Signals raised in the browser. The rest are server-side or bookkeeping. */
export const CLIENT_RAISED_EVENT_TYPES: readonly string[] = [
    ProctorEventTypes.TabSwitch,
    ProctorEventTypes.WindowBlur,
    ProctorEventTypes.FullscreenExit,
    ProctorEventTypes.Paste,
    ProctorEventTypes.ContextMenu,
    ProctorEventTypes.UnloadAttempt,
    ProctorEventTypes.AwayTooLong,
    ProctorEventTypes.NoFace,
    ProctorEventTypes.MultipleFaces,
    ProctorEventTypes.LookingAway,
    ProctorEventTypes.CameraLost,
    ProctorEventTypes.MultipleScreens,
    ProctorEventTypes.ScreenshotAttempt,
    ProctorEventTypes.BlockedKeyInput,
    ProctorEventTypes.PeriodicSample,
    ProctorEventTypes.Baseline
];

export const ProctorEventSources = {
    Client: 'Client',
    Server: 'Server'
} as const;

/** Case-insensitive membership, matching the C# StringComparer.OrdinalIgnoreCase checks. */
export const isClientRaised = (eventType: string): boolean =>
    CLIENT_RAISED_EVENT_TYPES.some((t) => t.toLowerCase() === (eventType ?? '').toLowerCase());

export const isConfigurable = (eventType: string): boolean =>
    CONFIGURABLE_EVENT_TYPES.some((t) => t.toLowerCase() === (eventType ?? '').toLowerCase());
